import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { FunctionComponent, useState } from 'react';

import { buttons } from '@/styles/buttons';

import { Avatar } from '@/ui/Avatars/Avatar';
import { AvatarStack } from '@/ui/Avatars/AvatarStack';
import { ChevronDownSmallIcon } from '@/ui/Icons/ChevronDownSmallIcon';
import type { IconProps } from '@/ui/Icons/IconProps';

import { MultiselectItem } from '..';

type TouchPosition = { x: number; y: number; time: number };

export type MultiselectTriggerProps = {
  /**
   * The current value of the multiselect input.
   */
  value: string[];
  /**
   * Default value of t he multiselect input.
   */
  defaultValue?: string[];
  /**
   * List of multiselect items.
   */
  items: MultiselectItem[];
  /**
   * Callback to toggle the multiselect open state.
   */
  toggleOpenState: () => void;
  /**
   * Variant of the multiselect trigger.
   */
  triggerVariant?: 'default' | 'secondary';
  /**
   * Attributes to display the correct label depending on the selection state.
   */
  label: { default: string; Icon?: FunctionComponent<IconProps>; entity: string };
};

const SELECTED_ITEM_LOGOS_LIMIT = 4;

export const MultiselectTrigger = ({
  value,
  defaultValue,
  items,
  toggleOpenState,
  triggerVariant,
  label,
}: MultiselectTriggerProps) => {
  const [touchStart, setTouchStart] = useState<TouchPosition | null>(null);

  const handlePointerDown = (event: React.PointerEvent) => {
    // For touch devices, prevent the default Radix behavior
    if (event.pointerType === 'touch') {
      event.preventDefault();
      setTouchStart({ x: event.clientX, y: event.clientY, time: Date.now() });
    }
  };

  const handlePointerUp = (event: React.PointerEvent) => {
    // Handle touch end - only open if it was a tap (not a swipe)
    if (event.pointerType !== 'touch' || !touchStart) {
      return;
    }

    const deltaX = Math.abs(event.clientX - touchStart.x);
    const deltaY = Math.abs(event.clientY - touchStart.y);
    const deltaTime = Date.now() - touchStart.time;

    // Consider it a tap if movement < 10px and time < 500ms
    if (deltaX < 10 && deltaY < 10 && deltaTime < 500) {
      toggleOpenState();
    }

    setTouchStart(null);
  };

  const selectedItems = value
    .map((value) => items.find((item) => item.value === value)!)
    .filter(Boolean);
  const selectedItemsWithIcon = selectedItems.filter((item) => item.icon != null);

  const showSelectedItemLogos = selectedItems.some((item) => item.icon != null);

  const hasSelectedState =
    value.length > 0 &&
    value.length !== items.length &&
    value.toString() !== defaultValue?.toString();

  return (
    <DropdownMenu.Trigger asChild>
      <button
        className={buttons({
          size: 'small',
          shape: 'rounded',
          color: 'gray',
          class: [
            'gap-2 flex justify-between cursor-pointer text-center pr-2 w-fit shrink-0',
            'data-[selected=true]:bg-token-purple-300 data-[selected=true]:text-token-purple-1100',
            'data-[selected=true]:shadow-purple data-[selected=true]:hover:bg-token-purple-400',
            'data-[selected=true]:active:bg-token-purple-400 data-[selected=true]:focus-visible:outline-[3px]',
            'data-[selected=true]:focus-visible:outline-token-purple-600',
            triggerVariant === 'secondary' &&
              'text-token-gray-900 bg-transparent data-[selected=false]:border-solid data-[selected=false]:border-1 border-token-alphalight-300 hover:bg-token-alphalight-100',
          ],
        })}
        data-selected={hasSelectedState}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        // Clean up if the pointer interaction was cancelled
        onPointerCancel={() => setTouchStart(null)}
      >
        {!hasSelectedState && (
          <div className="flex flex-row gap-2">
            {label.Icon && <label.Icon className="size-4" />}
            {label.default}
          </div>
        )}
        {hasSelectedState && (
          <div className="flex flex-row gap-1.5 items-center">
            {showSelectedItemLogos && (
              <div className="flex">
                <AvatarStack
                  size={18}
                  overlap={2}
                  borderRadius={selectedItemsWithIcon[0].iconRadius}
                  maxItems={SELECTED_ITEM_LOGOS_LIMIT}
                  badgeClassNames="bg-token-purple-400 border-token-purple-600 text-token-purple-1100"
                >
                  {selectedItemsWithIcon.map((item) => (
                    <Avatar
                      key={item.value}
                      src={item.icon}
                      fallbackSrc="/images/chains/none.svg"
                      size={18}
                      radius={item.iconRadius}
                    />
                  ))}
                </AvatarStack>
              </div>
            )}
            {!showSelectedItemLogos && label.Icon != null && <label.Icon className="size-4" />}
            {value.length === 1 ? selectedItems[0].label : `${value.length} ${label.entity}`}
          </div>
        )}
        <ChevronDownSmallIcon />
      </button>
    </DropdownMenu.Trigger>
  );
};
